import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CutoutPoint } from 'cloud-shared-lib';
import * as _ from 'lodash';

import { Observable, tap, timer } from 'rxjs';
import { SvgWorkspaceComponent } from '../../../components/svg-workspace/svg-workspace.component';
import { Guid } from '../../../helpers/guid.helper';
import { FastenerBasePointEnum } from '../../../models/cutting/cutting-custom-hole.model';
import { CuttingPanel } from '../../../models/cutting/cutting-panel.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { CuttingPanelListService } from '../cutting-panel-list.service';

@Component({
  selector: 'csl-panel-mirroring-dialog',
  templateUrl: './panel-mirroring-dialog.component.html',
  styleUrls: ['./panel-mirroring-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PanelMirroringDialogComponent implements OnInit {
  side: Position;
  _panel: CuttingPanel;
  mirrored: CuttingPanel;

  render$: Observable<number>;

  @Input() set panel(val: CuttingPanel) {
    this._panel = val;
    this.mirrored = _.cloneDeep(val);
    this.mirrored.position = '';
    this.mirrored.uuid = Guid.newGuid();

    if (!this.side) {
      this.selectSide('rc');
    }
  }

  get panel(): CuttingPanel {
    return this._panel;
  }

  @ViewChild('svgWorkspaceAfter', { static: true }) svgWorkspaceAfter: SvgWorkspaceComponent;
  @ViewChild('svgWorkspaceBefore', { static: true }) svgWorkspaceBefore: SvgWorkspaceComponent;

  constructor(
    public dialogRef: MatDialogRef<PanelMirroringDialogComponent>, //
    private cuttingPanelListService: CuttingPanelListService
  ) {}

  ngOnInit(): void {
    /* TODO: Т к раньше это делалось в ngAfterViewChecked, это очень перегружало основной поток и вызывало циклические рендеры,
     * чтобы ничего не поломать и снизить нагрузку добавлен этот костыль,
     * но перерисовку все равно необходимо переделать, чтобы схема перерисовывалась только при реальных изменениях в панели
     */
    this.render$ = timer(0, 1000).pipe(
      tap(() => {
        this.svgWorkspaceAfter.draw();
        this.svgWorkspaceBefore.draw();
      })
    );
  }

  selectSide(side: Position): void {
    this.side = side;
    this.mirrorPanel();
  }

  mirrorPanel(): void {
    switch (this.side) {
      case 'tc':
        this.horizontalMirror();

        this.svgWorkspaceAfter.draw(this.mirrored);
        break;

      case 'rc':
        this.verticalMirror();

        this.svgWorkspaceAfter.draw(this.mirrored);
        break;

      case 'bc':
        this.horizontalMirror();

        this.svgWorkspaceAfter.draw(this.mirrored);
        break;

      case 'lc':
        this.verticalMirror();

        this.svgWorkspaceAfter.draw(this.mirrored);
        break;
    }
  }

  private getMirroredCutoutPoint(point: CutoutPoint, axis: 'horizontal' | 'vertical'): CutoutPoint {
    if (axis === 'horizontal') {
      switch (point) {
        case CutoutPoint.TopLeft:
          return CutoutPoint.BottomLeft;
        case CutoutPoint.BottomLeft:
          return CutoutPoint.TopLeft;
        case CutoutPoint.TopRight:
          return CutoutPoint.BottomRight;
        case CutoutPoint.BottomRight:
          return CutoutPoint.TopRight;
      }
    }
    if (axis === 'vertical') {
      switch (point) {
        case CutoutPoint.TopLeft:
          return CutoutPoint.TopRight;
        case CutoutPoint.BottomLeft:
          return CutoutPoint.BottomRight;
        case CutoutPoint.TopRight:
          return CutoutPoint.TopLeft;
        case CutoutPoint.BottomRight:
          return CutoutPoint.BottomLeft;
      }
    }

    return CutoutPoint.Center;
  }

  horizontalMirror(): void {
    // cutouts
    this.mirrored.panelOutline.cutouts = _.cloneDeep(this.panel.panelOutline.cutouts);
    this.mirrored.panelOutline.cutouts.forEach(c => {
      c.basePoint = this.getMirroredCutoutPoint(c.basePoint, 'horizontal');
      c.angle = this.getMirroredCutoutPoint(c.angle, 'horizontal');
    });

    // slots
    this.mirrored.panelOutline.topSide.groove = _.cloneDeep(this.panel.panelOutline.bottomSide.groove);
    this.mirrored.panelOutline.bottomSide.groove = _.cloneDeep(this.panel.panelOutline.topSide.groove);
    this.mirrored.panelOutline.leftSide.groove = _.cloneDeep(this.panel.panelOutline.leftSide.groove);
    this.mirrored.panelOutline.rightSide.groove = _.cloneDeep(this.panel.panelOutline.rightSide.groove);

    // fasteners sets
    this.mirrored.panelOutline.topSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.bottomSide.fastenersSets);
    this.mirrored.panelOutline.topSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.bottomSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.topSide.fastenersSets);
    this.mirrored.panelOutline.bottomSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    // holes
    this.mirrored.panelOutline.topSide.customHoles = _.cloneDeep(this.panel.panelOutline.bottomSide.customHoles);
    this.mirrored.panelOutline.topSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.bottomSide.customHoles = _.cloneDeep(this.panel.panelOutline.topSide.customHoles);
    this.mirrored.panelOutline.bottomSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.leftSide.customHoles = _.cloneDeep(this.panel.panelOutline.leftSide.customHoles);
    this.mirrored.panelOutline.leftSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.rightSide.customHoles = _.cloneDeep(this.panel.panelOutline.rightSide.customHoles);
    this.mirrored.panelOutline.rightSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.leftSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.leftSide.fastenersSets);
    this.mirrored.panelOutline.leftSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.rightSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.rightSide.fastenersSets);
    this.mirrored.panelOutline.rightSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    // angles
    this.mirrored.panelOutline.topLeft = _.cloneDeep(this.panel.panelOutline.bottomLeft);
    this.mirrored.panelOutline.bottomLeft = _.cloneDeep(this.panel.panelOutline.topLeft);
    this.mirrored.panelOutline.topRight = _.cloneDeep(this.panel.panelOutline.bottomRight);
    this.mirrored.panelOutline.bottomRight = _.cloneDeep(this.panel.panelOutline.topRight);

    // butts
    this.mirrored.l1 = _.cloneDeep(this.panel.l2);
    this.mirrored.l2 = _.cloneDeep(this.panel.l1);
    this.mirrored.w1 = _.cloneDeep(this.panel.w1);
    this.mirrored.w2 = _.cloneDeep(this.panel.w2);
  }

  verticalMirror(): void {
    // cutouts
    this.mirrored.panelOutline.cutouts = _.cloneDeep(this.panel.panelOutline.cutouts);
    this.mirrored.panelOutline.cutouts.forEach(c => {
      c.basePoint = this.getMirroredCutoutPoint(c.basePoint, 'vertical');
      c.angle = this.getMirroredCutoutPoint(c.angle, 'vertical');
    });

    // slots
    this.mirrored.panelOutline.leftSide.groove = _.cloneDeep(this.panel.panelOutline.rightSide.groove);
    this.mirrored.panelOutline.rightSide.groove = _.cloneDeep(this.panel.panelOutline.leftSide.groove);
    this.mirrored.panelOutline.topSide.groove = _.cloneDeep(this.panel.panelOutline.topSide.groove);
    this.mirrored.panelOutline.bottomSide.groove = _.cloneDeep(this.panel.panelOutline.bottomSide.groove);

    // fastener sets
    this.mirrored.panelOutline.topSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.topSide.fastenersSets);
    this.mirrored.panelOutline.topSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.bottomSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.bottomSide.fastenersSets);
    this.mirrored.panelOutline.bottomSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.leftSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.rightSide.fastenersSets);
    this.mirrored.panelOutline.leftSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.rightSide.fastenersSets = _.cloneDeep(this.panel.panelOutline.leftSide.fastenersSets);
    this.mirrored.panelOutline.rightSide.fastenersSets.forEach(
      s =>
        (s.basePoint =
          s.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    // holes
    this.mirrored.panelOutline.topSide.customHoles = _.cloneDeep(this.panel.panelOutline.topSide.customHoles);
    this.mirrored.panelOutline.topSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.bottomSide.customHoles = _.cloneDeep(this.panel.panelOutline.bottomSide.customHoles);
    this.mirrored.panelOutline.bottomSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    this.mirrored.panelOutline.leftSide.customHoles = _.cloneDeep(this.panel.panelOutline.rightSide.customHoles);
    this.mirrored.panelOutline.leftSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );
    this.mirrored.panelOutline.rightSide.customHoles = _.cloneDeep(this.panel.panelOutline.leftSide.customHoles);
    this.mirrored.panelOutline.rightSide.customHoles.forEach(
      h =>
        (h.basePoint =
          h.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End)
    );

    // angles
    this.mirrored.panelOutline.topLeft = _.cloneDeep(this.panel.panelOutline.topRight);
    this.mirrored.panelOutline.topRight = _.cloneDeep(this.panel.panelOutline.topLeft);

    this.mirrored.panelOutline.bottomRight = _.cloneDeep(this.panel.panelOutline.bottomLeft);
    this.mirrored.panelOutline.bottomLeft = _.cloneDeep(this.panel.panelOutline.bottomRight);

    // butts
    this.mirrored.l1 = _.cloneDeep(this.panel.l1);
    this.mirrored.l2 = _.cloneDeep(this.panel.l2);
    this.mirrored.w1 = _.cloneDeep(this.panel.w2);
    this.mirrored.w2 = _.cloneDeep(this.panel.w1);
  }
}
